// SettingsDrawer.tsx — Item 12, the SUMMONED settings drawer (the broader SETTINGS pane).
// CONTRACT: docs/ux/SHELL-COMPOSITION.md §2.9
//
// A transient drawer (over NO persistent furniture, never occluding the map) with FOUR tabs:
//
//   IDENTITY tab — the operator's own presentation (I8 v4, identity-scoped leaves):
//     - avatar preview (Google picture / deterministic initials — Avatar.tsx)
//     - callsign (the name printed on the map, in chat, in presence) → settings.user.callsign
//     - "use real name" opt-in              → settings.user.useRealName
//     Identity leaves only write into a persona scope — with no signed-in identity the
//     controls are disabled (a device-scope write would bleed across users, so the store
//     drops it; the UI says so instead of failing silently).
//
//   DISPLAY tab — the everyday display preferences:
//     - render profile / theme (day / night / NVG / intel + TAK + Civic + high-contrast)
//                                           → handle.setRenderProfile(themeId)
//     - 2525 frames ⇄ dots symbology        → handle.setMarkerMode(mode)
//     - device-class layout recipe          → handle.setLayout(recipe)
//     - reduced-motion preference (UI-only) → applyReducedMotion() on :root
//     - affiliation fade opacity (neutral / unknown declutter)  → settings.fade.*
//
//   ADVANCED tab — Bundle A's render params (CONTRACT:I8-LOCAL-SETTINGS.1.0 keys):
//     - velocity-vector / tail scale        → settings.advanced.vectorScale
//     - velocity-correlated error ellipses  → settings.advanced.ellipse.*
//     - air drop-line gates                 → settings.advanced.dropLine.*
//
//   PRESENCE tab — opt-in broadcast preferences (I8 v4; the presence ENGINE is a separate
//   lane — these leaves are read by it with fallbacks when it ships):
//     - share my location (STRICTLY opt-in, default OFF) → settings.presence.shareLocation
//     - update interval (5..120 s)                        → settings.presence.updateSeconds
//
//   (No ALERTS tab: the alert-ack leaves are id ARRAYS owned by the AlertsPanel's own
//   mark-read / clear affordances — there is no natural drawer control for them.)
//
// PERSISTENCE: every choice is written to the @tak-tdf/local-settings store (per-persona with a
// device fallback) so it survives a reload. The host remains the live source of truth for the
// render profile / marker / layout (the drawer reads status.* for the active checkmark AND writes
// the choice to the store); the boot re-apply of persisted settings is wired in cop.ts/App.tsx.
//
// It REGISTERS as the R-LAW-3 heavy panel ("settings") via useHeavyPanel, so summoning it
// collapses any other heavy surface; and it pops on Esc via useEscapeStack.
//
// DISPLAY-ONLY INVARIANT: nothing in this drawer changes WHAT is visible (the projection / PDP
// outcome) — only HOW it is drawn. The settings store carries no visibility/clearance field.
//
// GLOMAR / denial: this component renders no per-object aggregate, so there is nothing to leak.
// Active-state checkmarks read status.renderProfile / status.markerMode / status.layout DIRECTLY
// (always-present per GROUP 0).

import { useEffect, useState } from "react";
import type { CopHandle, CopStatus } from "../host/cop.ts";
import { applyReducedMotion } from "../host/theme.ts";
import { tdflite } from "../host/tdflite.ts";
import { displayNameFor } from "../host/personas.ts";
import {
  getSettingsStore,
  CALLSIGN_MAX_LENGTH,
  PRESENCE_UPDATE_MIN_S,
  PRESENCE_UPDATE_MAX_S,
} from "../../../../packages/local-settings/src/index.ts";
import type { Settings, SettingPath } from "../../../../packages/local-settings/src/index.ts";
import { Avatar } from "./Avatar.tsx";
import { useHeavyPanel } from "./heavyPanel.ts";
import { useEscapeStack } from "./useEscapeStack.ts";
import "./settings.css";

/** A render-profile option — the operator-facing framing of the shipped THEMES ids. `themeId`
 *  is passed verbatim to handle.setRenderProfile AND persisted as settings.renderProfile. */
interface ProfileOption {
  themeId: string;
  label: string;
  sub: string;
}

/** Render profiles / themes over the underlying THEMES ids. 'Day' is the TRUE LIGHT theme; cop.ts
 *  setRenderProfile swaps the basemap via basemapUrlForProfile alongside applyTheme. The two added
 *  themes (TAK + Civic) ride the same mechanism. NOTE: the "Civic" label is a PLACEHOLDER name —
 *  it is centralised on this one row so renaming the consumer tier is a one-line change. */
const PROFILES: readonly ProfileOption[] = [
  { themeId: "dark", label: "Night", sub: "Dark tactical — the resting profile" },
  { themeId: "light", label: "Day", sub: "True light — Positron basemap for bright-light ops" },
  { themeId: "mono-nvg", label: "NVG", sub: "Monochrome night-vision safe" },
  { themeId: "intel-skin", label: "Intel", sub: "Analyst command-grade skin" },
  { themeId: "tak", label: "TAK", sub: "ATAK / WinTAK-familiar palette" },
  { themeId: "civic", label: "Everyday", sub: "Consumer tier — light, approachable" },
  { themeId: "high-contrast", label: "High contrast", sub: "Raised contrast for bright-light ops" },
];

/** Symbology carrier options — three-way: minimal dots / 2525-style frames / full
 *  milsymbol 2525 symbols. The INTERNAL values stay "dot" / "milsym" (persisted blobs
 *  remain valid — no value migration); only the operator-facing labels are new. */
const MARKER_MODES: readonly { mode: "milsym" | "dot" | "coalition"; label: string; sub: string }[] = [
  { mode: "dot", label: "Minimal", sub: "Clean low-ink dots — best at density" },
  { mode: "milsym", label: "Standard", sub: "2525-style affiliation frames" },
  { mode: "coalition", label: "Coalition", sub: "Full MIL-STD-2525 symbols" },
];

/** Device-class layout recipes. */
const LAYOUTS: readonly { recipe: CopStatus["layout"]; label: string; sub: string }[] = [
  { recipe: "cockpit", label: "Cockpit", sub: "Both gutters available" },
  { recipe: "field", label: "Field", sub: "Icon-rail, bottom-detent" },
  { recipe: "workbench", label: "Workbench", sub: "Wide analyst desk" },
];

type Tab = "identity" | "display" | "advanced" | "presence";

/** The tab strip, in reading order. Display stays the LANDING tab (the everyday surface —
 *  existing muscle memory); Identity leads the strip per the section ordering. */
const TABS: readonly { id: Tab; label: string }[] = [
  { id: "identity", label: "Identity" },
  { id: "display", label: "Display" },
  { id: "advanced", label: "Advanced" },
  { id: "presence", label: "Presence" },
];

export interface SettingsDrawerProps {
  status: CopStatus;
  handle: CopHandle | null;
}

/** A labelled range slider that writes one settings leaf path on change + shows the live value. */
function RangeRow(props: {
  label: string;
  sub: string;
  path: SettingPath;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (path: SettingPath, value: number) => void;
}): JSX.Element {
  const { label, sub, path, value, min, max, step, format, onChange } = props;
  return (
    <label className="settings__range">
      <span className="settings__range-head">
        <span className="settings__opt-label">{label}</span>
        <span className="settings__range-value">{format(value)}</span>
      </span>
      <input
        type="range"
        className="settings__range-input"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(path, Number(e.currentTarget.value))}
      />
      <span className="settings__opt-sub">{sub}</span>
    </label>
  );
}

const pct = (v: number): string => `${Math.round(v * 100)}%`;
const num = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(1));

/**
 * The summoned SettingsDrawer. Renders nothing at rest; when summoned (it holds the "settings"
 * heavy slot) it slides in a right-edge drawer over a dismiss scrim. Esc / scrim-click / the close
 * affordance all pop exactly one level.
 */
export function SettingsDrawer({ status, handle }: SettingsDrawerProps): JSX.Element | null {
  const heavy = useHeavyPanel("settings");
  const open = heavy.isActive;

  // Esc pops THIS level only while the drawer is open (LIFO via the shared stack).
  useEscapeStack(() => heavy.release(), open);

  const store = getSettingsStore();

  // The live effective settings snapshot (per-persona with device fallback). Subscribed once;
  // re-synced + reconciled to the :root reduced-motion attr each time the drawer opens.
  const [settings, setSettings] = useState<Settings>(() => store.getSettings());
  const [tab, setTab] = useState<Tab>("display");

  useEffect(() => {
    const unsub = store.subscribe(setSettings);
    return unsub;
  }, [store]);

  useEffect(() => {
    if (!open) return;
    const s = store.getSettings();
    setSettings(s);
    // Reconcile the :root force-reduced-motion attribute with the persisted preference on open
    // (so a persisted "reduce motion" is honoured even before the boot re-apply lands).
    applyReducedMotion(s.reducedMotion);
  }, [open, store]);

  if (!open) return null;

  const activeProfile = status.renderProfile;
  const activeLayout = status.layout;
  const activeMarker = status.markerMode;
  const forceReduced = settings.reducedMotion;

  // --- IDENTITY derivation (display-only; the JWT username stays the security subject) ---
  // The store's active persona IS the logged-in username (App.tsx setPersona on login).
  const identityUser = store.getActivePersona();
  const session = tdflite.peek();
  const sessionIsCurrent = identityUser !== null && session?.username === identityUser;
  const realName =
    (sessionIsCurrent ? session?.name : undefined) ??
    (displayNameFor(identityUser ?? "") || "Operator");
  const callsignSet = settings.user.callsign.trim();
  const effectiveName =
    settings.user.useRealName || callsignSet === "" ? realName : callsignSet;
  const picture = sessionIsCurrent ? session?.picture : undefined;

  /** Persist a render-profile choice + drive the host (live theme/basemap swap). */
  const chooseProfile = (themeId: string): void => {
    handle?.setRenderProfile(themeId);
    store.setSetting("renderProfile", themeId);
  };
  const chooseMarker = (mode: "milsym" | "dot" | "coalition"): void => {
    handle?.setMarkerMode(mode);
    store.setSetting("markerMode", mode);
  };
  const chooseLayout = (recipe: CopStatus["layout"]): void => {
    handle?.setLayout(recipe);
    store.setSetting("layout", recipe);
  };
  const toggleReduced = (): void => {
    const next = !settings.reducedMotion;
    store.setSetting("reducedMotion", next);
    applyReducedMotion(next);
  };
  /** Flip one of the icon-density-LOD booleans. The write notifies the store, and the
   *  host's settings subscription (cop.ts) re-pushes the blob to the render backend —
   *  which folds the toggle in and rebuilds the deck (same live path the fade sliders ride). */
  const toggleDensity = (path: "iconDensityLod" | "blueForceAlwaysProminent"): void => {
    store.setSetting(path, !settings[path]);
  };
  const setNumber = (path: SettingPath, value: number): void => {
    store.setSetting(path, value as never);
  };
  /** Write the callsign as typed: strip control/line chars + clamp to the validator's cap
   *  BEFORE the store write (the input also carries maxLength, so the clamp is belt-and-
   *  braces against paste). Identity-scoped — the input is disabled with no persona, so
   *  the store's silent device-bleed drop is never hit from here. */
  const setCallsign = (raw: string): void => {
    // eslint-disable-next-line no-control-regex
    const cleaned = raw.replace(/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g, "");
    store.setSetting("user.callsign", cleaned.slice(0, CALLSIGN_MAX_LENGTH));
  };
  const toggleUseRealName = (): void => {
    store.setSetting("user.useRealName", !settings.user.useRealName);
  };
  /** Location sharing is a LIVE control, not a silent preference write: turning it ON runs the
   *  host's DOUBLE-CONSENT flow (our dialog → the browser geolocation prompt) and only then
   *  captures + emits the operator's position; OFF stops the watch immediately. The persisted leaf
   *  is reconciled to the ACTUAL resulting state — FAIL-CLOSED: a denied/failed consent resolves
   *  false, so the switch snaps back to OFF and the leaf never claims "sharing on" without a live
   *  fix. status.shareLocation is the host's live truth the switch reflects (consent is per-session;
   *  a persisted preference never auto-starts sharing at boot). */
  const toggleShareLocation = (): void => {
    const next = !status.shareLocation;
    if (!handle) {
      // No live host to capture/emit a position — never assert "sharing on" without one.
      if (!next) store.setSetting("presence.shareLocation", false);
      return;
    }
    void handle.setShareLocation(next).then((actual) => {
      store.setSetting("presence.shareLocation", actual);
    });
  };

  return (
    <div className="settings" role="presentation">
      <div className="settings__scrim" onClick={() => heavy.release()} aria-hidden="true" />
      <aside
        className="settings__drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
      >
        <header className="settings__head">
          <h2 className="panel__title settings__title">Settings</h2>
          <button
            type="button"
            className="settings__close"
            onClick={() => heavy.release()}
            aria-label="Close settings (Esc)"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        {/* ---- tab strip: Identity / Display / Advanced / Presence ---- */}
        <div className="settings__tabs" role="tablist" aria-label="Settings sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`settings-tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`settings-panel-${t.id}`}
              className="settings__tab"
              data-on={tab === t.id || undefined}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "identity" && (
          <div
            className="settings__body"
            id="settings-panel-identity"
            role="tabpanel"
            aria-labelledby="settings-tab-identity"
          >
            {/* ---- Operator: avatar + effective display name preview ---- */}
            <section className="settings__group" aria-label="Operator">
              <h3 className="settings__group-title">Operator</h3>
              <div className="settings__identity">
                <Avatar
                  username={identityUser ?? "operator"}
                  displayName={effectiveName}
                  picture={picture}
                  size={40}
                />
                <div className="settings__identity-text">
                  <span className="settings__identity-name">{effectiveName}</span>
                  <span className="settings__opt-sub">
                    {identityUser ?? "no signed-in identity"}
                  </span>
                </div>
              </div>
              <span className="settings__opt-sub">
                {picture
                  ? "Your Google profile picture carries your marker; the disc colour backs it up offline."
                  : "Your avatar is a stable colour derived from your identity — the same on every device."}
              </span>
            </section>

            {/* ---- Callsign + real-name opt-in (identity-scoped I8 leaves) ---- */}
            <section className="settings__group" aria-label="Callsign">
              <h3 className="settings__group-title">Callsign</h3>
              {identityUser ? (
                <>
                  <label className="settings__field">
                    <input
                      type="text"
                      className="settings__input"
                      value={settings.user.callsign}
                      maxLength={CALLSIGN_MAX_LENGTH}
                      placeholder={realName}
                      aria-label="Callsign"
                      spellCheck={false}
                      autoComplete="off"
                      onChange={(e) => setCallsign(e.currentTarget.value)}
                    />
                    <span className="settings__opt-sub">
                      The name printed on the map, in chat, and anywhere you speak. Empty =
                      your account name.
                    </span>
                  </label>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={settings.user.useRealName}
                    className="settings__toggle"
                    data-on={settings.user.useRealName || undefined}
                    onClick={toggleUseRealName}
                  >
                    <span className="settings__toggle-track" aria-hidden="true">
                      <span className="settings__toggle-knob" />
                    </span>
                    <span className="settings__opt-text">
                      <span className="settings__opt-label">Use real name</span>
                      <span className="settings__opt-sub">
                        Show {realName} everywhere, even when a callsign is set
                      </span>
                    </span>
                  </button>
                </>
              ) : (
                <span className="settings__opt-sub">
                  Sign in to set a callsign — identity preferences are stored per identity,
                  never on the shared device.
                </span>
              )}
            </section>
          </div>
        )}

        {tab === "presence" && (
          <div
            className="settings__body"
            id="settings-panel-presence"
            role="tabpanel"
            aria-labelledby="settings-tab-presence"
          >
            {/* ---- Location sharing (STRICTLY opt-in; the engine is a separate lane) ---- */}
            <section className="settings__group" aria-label="Location sharing">
              <h3 className="settings__group-title">Location sharing</h3>
              <button
                type="button"
                role="switch"
                aria-checked={status.shareLocation}
                className="settings__toggle"
                data-on={status.shareLocation || undefined}
                onClick={toggleShareLocation}
              >
                <span className="settings__toggle-track" aria-hidden="true">
                  <span className="settings__toggle-knob" />
                </span>
                <span className="settings__opt-text">
                  <span className="settings__opt-label">Share my location</span>
                  <span className="settings__opt-sub">
                    Broadcast your position into the team picture. Off by default — nothing
                    is sent until you turn this on.
                  </span>
                </span>
              </button>
              <div className="settings__ranges">
                <RangeRow
                  label="Update interval"
                  sub="How often your position refreshes for the team"
                  path="presence.updateSeconds"
                  value={settings.presence.updateSeconds}
                  min={PRESENCE_UPDATE_MIN_S}
                  max={PRESENCE_UPDATE_MAX_S}
                  step={5}
                  format={(v) => `${num(v)} s`}
                  onChange={setNumber}
                />
              </div>
              <span className="settings__opt-sub">
                Sharing is per-session and consented: turning it on asks you (a dialog, then your
                browser's location prompt) before any position is sent; turning it off — or a
                denied or lost fix — stops it at once.
              </span>
            </section>
          </div>
        )}

        {tab === "display" && (
          <div
            className="settings__body"
            id="settings-panel-display"
            role="tabpanel"
            aria-labelledby="settings-tab-display"
          >
            {/* ---- Render profile / theme ---- */}
            <section className="settings__group" aria-label="Render profile">
              <h3 className="settings__group-title">Render profile</h3>
              <div className="settings__opts" role="radiogroup" aria-label="Render profile">
                {PROFILES.map((p) => {
                  const on = p.themeId === activeProfile;
                  return (
                    <button
                      key={p.themeId}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      className="settings__opt"
                      data-on={on || undefined}
                      onClick={() => chooseProfile(p.themeId)}
                    >
                      <span className="settings__opt-mark" aria-hidden="true" />
                      <span className="settings__opt-text">
                        <span className="settings__opt-label">{p.label}</span>
                        <span className="settings__opt-sub">{p.sub}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ---- Symbology carrier ---- */}
            <section className="settings__group" aria-label="Symbology">
              <h3 className="settings__group-title">Symbology</h3>
              <div className="settings__opts" role="radiogroup" aria-label="Symbology carrier">
                {MARKER_MODES.map((m) => {
                  const on = m.mode === activeMarker;
                  return (
                    <button
                      key={m.mode}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      className="settings__opt"
                      data-on={on || undefined}
                      onClick={() => chooseMarker(m.mode)}
                    >
                      <span className="settings__opt-mark" aria-hidden="true" />
                      <span className="settings__opt-text">
                        <span className="settings__opt-label">{m.label}</span>
                        <span className="settings__opt-sub">{m.sub}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ---- Device-class layout recipe ---- */}
            <section className="settings__group" aria-label="Layout">
              <h3 className="settings__group-title">Layout</h3>
              <div className="settings__opts" role="radiogroup" aria-label="Layout recipe">
                {LAYOUTS.map((l) => {
                  const on = l.recipe === activeLayout;
                  return (
                    <button
                      key={l.recipe}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      className="settings__opt"
                      data-on={on || undefined}
                      onClick={() => chooseLayout(l.recipe)}
                    >
                      <span className="settings__opt-mark" aria-hidden="true" />
                      <span className="settings__opt-text">
                        <span className="settings__opt-label">{l.label}</span>
                        <span className="settings__opt-sub">{l.sub}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ---- Affiliation fade (declutter) ---- */}
            <section className="settings__group" aria-label="Fade">
              <h3 className="settings__group-title">Fade</h3>
              <div className="settings__ranges">
                <RangeRow
                  label="Neutral opacity"
                  sub="Fade neutral-affiliation markers to cut clutter"
                  path="fade.neutral"
                  value={settings.fade.neutral}
                  min={0}
                  max={1}
                  step={0.05}
                  format={pct}
                  onChange={setNumber}
                />
                <RangeRow
                  label="Unknown opacity"
                  sub="Fade unknown-affiliation markers"
                  path="fade.unknown"
                  value={settings.fade.unknown}
                  min={0}
                  max={1}
                  step={0.05}
                  format={pct}
                  onChange={setNumber}
                />
              </div>
            </section>

            {/* ---- Icon-density LOD (declutter under load) ---- */}
            <section className="settings__group" aria-label="Density">
              <h3 className="settings__group-title">Density</h3>
              <button
                type="button"
                role="switch"
                aria-checked={settings.iconDensityLod}
                className="settings__toggle"
                data-on={settings.iconDensityLod || undefined}
                onClick={() => toggleDensity("iconDensityLod")}
              >
                <span className="settings__toggle-track" aria-hidden="true">
                  <span className="settings__toggle-knob" />
                </span>
                <span className="settings__opt-text">
                  <span className="settings__opt-label">Icon density LOD</span>
                  <span className="settings__opt-sub">
                    In dense views, fade + shrink lower-priority icons by affiliation rank
                  </span>
                </span>
              </button>
              <button
                type="button"
                role="switch"
                aria-checked={settings.blueForceAlwaysProminent}
                className="settings__toggle"
                data-on={settings.blueForceAlwaysProminent || undefined}
                onClick={() => toggleDensity("blueForceAlwaysProminent")}
              >
                <span className="settings__toggle-track" aria-hidden="true">
                  <span className="settings__toggle-knob" />
                </span>
                <span className="settings__opt-text">
                  <span className="settings__opt-label">Blue force always prominent</span>
                  <span className="settings__opt-sub">
                    Own force never fades or shrinks under the density LOD
                  </span>
                </span>
              </button>
            </section>

            {/* ---- Reduced-motion preference (UI-only) ---- */}
            <section className="settings__group" aria-label="Motion">
              <h3 className="settings__group-title">Motion</h3>
              <button
                type="button"
                role="switch"
                aria-checked={forceReduced}
                className="settings__toggle"
                data-on={forceReduced || undefined}
                onClick={toggleReduced}
              >
                <span className="settings__toggle-track" aria-hidden="true">
                  <span className="settings__toggle-knob" />
                </span>
                <span className="settings__opt-text">
                  <span className="settings__opt-label">Reduce motion</span>
                  <span className="settings__opt-sub">
                    Only danger moves — suppress all other animation
                  </span>
                </span>
              </button>
            </section>
          </div>
        )}

        {tab === "advanced" && (
          <div
            className="settings__body"
            id="settings-panel-advanced"
            role="tabpanel"
            aria-labelledby="settings-tab-advanced"
          >
            {/* ---- Velocity vectors / tails ---- */}
            <section className="settings__group" aria-label="Velocity vectors">
              <h3 className="settings__group-title">Velocity vectors</h3>
              <div className="settings__ranges">
                <RangeRow
                  label="Vector + tail scale"
                  sub="Multiplier on the heading leader + comet-trail length"
                  path="advanced.vectorScale"
                  value={settings.advanced.vectorScale}
                  min={0}
                  max={4}
                  step={0.1}
                  format={(v) => `${num(v)}×`}
                  onChange={setNumber}
                />
              </div>
            </section>

            {/* ---- Error ellipses (per-domain uncertainty) ---- */}
            <section className="settings__group" aria-label="Error ellipses">
              <h3 className="settings__group-title">Error ellipses</h3>
              <div className="settings__ranges">
                <RangeRow
                  label="Ship σ"
                  sub="Maritime position uncertainty (tight)"
                  path="advanced.ellipse.shipSigma"
                  value={settings.advanced.ellipse.shipSigma}
                  min={0}
                  max={3}
                  step={0.1}
                  format={num}
                  onChange={setNumber}
                />
                <RangeRow
                  label="Air σ"
                  sub="Identified-air position uncertainty"
                  path="advanced.ellipse.airSigma"
                  value={settings.advanced.ellipse.airSigma}
                  min={0}
                  max={6}
                  step={0.1}
                  format={num}
                  onChange={setNumber}
                />
                <RangeRow
                  label="Unknown-air σ"
                  sub="Unknown / hostile air position uncertainty (wide)"
                  path="advanced.ellipse.unknownAirSigma"
                  value={settings.advanced.ellipse.unknownAirSigma}
                  min={0}
                  max={10}
                  step={0.5}
                  format={num}
                  onChange={setNumber}
                />
              </div>
            </section>

            {/* ---- Air drop-lines ---- */}
            <section className="settings__group" aria-label="Air drop-lines">
              <h3 className="settings__group-title">Air drop-lines</h3>
              <div className="settings__ranges">
                <RangeRow
                  label="Min zoom"
                  sub="Only draw stalks at/above this map zoom"
                  path="advanced.dropLine.minZoom"
                  value={settings.advanced.dropLine.minZoom}
                  min={0}
                  max={14}
                  step={1}
                  format={num}
                  onChange={setNumber}
                />
                <RangeRow
                  label="Min altitude"
                  sub="Only stalk assets at/above this altitude (m)"
                  path="advanced.dropLine.minAltitudeM"
                  value={settings.advanced.dropLine.minAltitudeM}
                  min={0}
                  max={12000}
                  step={250}
                  format={(v) => `${num(v)} m`}
                  onChange={setNumber}
                />
                <RangeRow
                  label="Ground-dot opacity"
                  sub="Dim ground anchor dot"
                  path="advanced.dropLine.groundDotOpacity"
                  value={settings.advanced.dropLine.groundDotOpacity}
                  min={0}
                  max={1}
                  step={0.05}
                  format={pct}
                  onChange={setNumber}
                />
                <RangeRow
                  label="Line opacity"
                  sub="Dim the vertical stalk line"
                  path="advanced.dropLine.lineOpacity"
                  value={settings.advanced.dropLine.lineOpacity}
                  min={0}
                  max={1}
                  step={0.05}
                  format={pct}
                  onChange={setNumber}
                />
              </div>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}
